import { JSX } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Datagrid,
    DateField,
    FunctionField,
    List,
    SelectField,
    SelectInput,
    TextField,
} from 'react-admin';
import { Chip } from '@mui/material';
import { SalesConversation } from '../../types/entities';

/**
 * Every email conversation with a contact form or enterprise request submitter, in one place.
 *
 * Conversations are worked from the record they belong to, so this is a way in rather than an
 * editor: each row links to its request's Conversation tab. There is no create — a thread is
 * started by the first reply or note — and no edit, which is why the data provider behind it
 * implements only the three read methods.
 */

const conversationStatus: { id: string, name: string }[] = [
    { id: 'open', name: 'Open' },
    { id: 'awaiting_reply', name: 'Awaiting their reply' },
    { id: 'closed', name: 'Closed' },
];

const filters: JSX.Element[] = [
    <SelectInput key="status" source="status" choices={ conversationStatus } alwaysOn />,
];

/**
 * The HQ route to the record this conversation belongs to, on its Conversation tab.
 *
 * Exported for the Email Messages show screen, which links a sales message to its thread.
 */
export const routeFor = (record: SalesConversation): string|null => {
    if (record.subjectId == null) {
        return null;
    }
    const resource: string = record.subjectType === 'enterprise_request'
        ? 'enterprise_requests'
        : 'contact_forms';

    // The trailing `/1` is the tab index.
    return `/${ resource }/${ record.subjectId }/1`;
};

export const SalesConversationList: () => JSX.Element = (): JSX.Element => (
    <List
        filters={ filters }
        // The thread that moved most recently is the one most likely to need something.
        sort={ { field: 'lastMessageAt', order: 'DESC' } }
        actions={ false }
    >
        {/* HQ-22 asked why this grid carries no permission check. It needs none and adding one
            would be duplicated logic that can drift: the screen is read only, `actions={false}`
            removes the toolbar, `rowClick={false}` and `bulkActionButtons={false}` remove the rest,
            so there is no control here to withhold. Reaching the route at all needs
            HQ_Sales::conversation, which react-admin refuses through `authProvider.canAccess` and
            HQ-API refuses again on the collection. */}
        <Datagrid bulkActionButtons={ false } rowClick={ false }>
            <TextField source="id" />
            <FunctionField
                label="From"
                render={ (record: SalesConversation): string => record.recipientName || 'Unknown' }
            />
            <FunctionField
                label="Type"
                render={ (record: SalesConversation): string =>
                    record.subjectType === 'enterprise_request' ? 'Enterprise' : 'Contact form' }
            />
            <TextField source="subject" />
            <SelectField source="status" choices={ conversationStatus } />
            <FunctionField
                label="Assigned to"
                render={ (record: SalesConversation): JSX.Element => (
                    // Unassigned is the state worth spotting: nobody has picked the thread up.
                    <Chip
                        size="small"
                        variant={ record.assignedTo ? 'filled' : 'outlined' }
                        color={ record.assignedTo ? 'primary' : 'default' }
                        label={ record.assignedTo?.name ?? 'Unassigned' }
                    />
                ) }
            />
            <DateField source="lastMessageAt" label="Last message" showTime />
            <FunctionField
                label=""
                render={ (record: SalesConversation): JSX.Element|null => {
                    const route: string|null = routeFor(record);

                    return route === null ? null : <RouterLink to={ route }>{ 'Open' }</RouterLink>;
                } }
            />
        </Datagrid>
    </List>
);
